/*
 * Accordion-style category tabs for MN Creative services.
 * - Hover (fine pointer) expands a tab
 * - Click follows the card link when set; otherwise only expands
 */
class MnCreativeServicesTabs extends HTMLElement {
  constructor() {
    super();
    this.handleClick = this.handleClick.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
    this.handlePointerEnter = this.handlePointerEnter.bind(this);
    this.hoverMedia = window.matchMedia('(hover: hover) and (pointer: fine)');
  }

  connectedCallback() {
    this.addEventListener('click', this.handleClick);
    this.addEventListener('keydown', this.handleKeydown);
    this.addEventListener('pointerenter', this.handlePointerEnter, true);

    const active = this.querySelector('[data-mn-creative-services-tab].is-active');
    const first = this.querySelector('[data-mn-creative-services-tab]');
    const initial = active instanceof HTMLElement ? active : first;

    this.lockPanelHeight();

    if (initial instanceof HTMLElement) {
      this.activate(initial, false);
    } else {
      this.classList.add('is-ready');
    }
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleClick);
    this.removeEventListener('keydown', this.handleKeydown);
    this.removeEventListener('pointerenter', this.handlePointerEnter, true);
  }

  handlePointerEnter(event) {
    if (!this.hoverMedia.matches) return;
    const target = event.target;
    if (!(target instanceof Element)) return;

    const tab = target.closest('[data-mn-creative-services-tab]');
    if (!(tab instanceof HTMLElement) || !this.contains(tab)) return;

    this.activate(tab, true);
  }

  handleClick(event) {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const tab = target.closest('[data-mn-creative-services-tab]');
    if (!(tab instanceof HTMLElement) || !this.contains(tab)) return;

    this.activate(tab, true);

    /* Linked cards (<a href>) navigate normally — do not preventDefault. */
    if (tab instanceof HTMLAnchorElement && tab.getAttribute('href')) {
      return;
    }

    event.preventDefault();
  }

  handleKeydown(event) {
    const tabs = this.tabs();
    if (!tabs.length) return;

    const active = document.activeElement;
    const current = active && active.closest ? active.closest('[data-mn-creative-services-tab]') : null;
    const index = tabs.indexOf(current);
    if (index < 0) return;

    let nextIndex = index;

    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowRight':
        event.preventDefault();
        nextIndex = (index + 1) % tabs.length;
        break;
      case 'ArrowUp':
      case 'ArrowLeft':
        event.preventDefault();
        nextIndex = (index - 1 + tabs.length) % tabs.length;
        break;
      case 'Home':
        event.preventDefault();
        nextIndex = 0;
        break;
      case 'End':
        event.preventDefault();
        nextIndex = tabs.length - 1;
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        this.activate(tabs[index], true);
        if (tabs[index] instanceof HTMLAnchorElement && tabs[index].href) {
          if (tabs[index].target === '_blank') {
            window.open(tabs[index].href, '_blank', 'noopener,noreferrer');
          } else {
            window.location.href = tabs[index].href;
          }
        }
        return;
      default:
        return;
    }

    tabs[nextIndex].focus();
    this.activate(tabs[nextIndex], true);
  }

  tabs() {
    return Array.from(this.querySelectorAll('[data-mn-creative-services-tab]')).filter(
      (el) => el instanceof HTMLElement
    );
  }

  /* Keep panel height fixed so accordion easing can’t grow the section. */
  lockPanelHeight() {
    const root = this.closest('.mn-creative-services');
    if (!(root instanceof HTMLElement)) return;

    const count = Math.max(this.tabs().length, 1);
    root.style.setProperty('--mn-creative-services-card-count', String(count));
  }

  /* Progressive wash: farther from the active tab = more faded. */
  washForDistance(distance) {
    if (distance <= 0) return 0;
    return Math.min(0.38 + distance * 0.22, 0.82);
  }

  activate(tab, animate) {
    const tabs = this.tabs();
    const activeIndex = tabs.indexOf(tab);
    if (activeIndex < 0) return;

    if (!animate) this.classList.add('is-ready');

    tabs.forEach((el, index) => {
      const isActive = el === tab;
      const distance = Math.abs(index - activeIndex);
      const wash = this.washForDistance(distance);

      el.classList.toggle('is-active', isActive);
      el.setAttribute('aria-selected', isActive ? 'true' : 'false');
      el.setAttribute('aria-expanded', isActive ? 'true' : 'false');
      el.setAttribute('tabindex', isActive ? '0' : '-1');
      el.style.setProperty('--mn-creative-services-wash', String(wash));
    });

    if (!animate) {
      requestAnimationFrame(() => this.classList.add('is-ready'));
    }
  }
}

if (!customElements.get('mn-creative-services-tabs')) {
  customElements.define('mn-creative-services-tabs', MnCreativeServicesTabs);
}
